// This is imported from client V1

import {
  cadTypes,
  getFileExtension,
  getFileMeta,
  getFilesMeta,
  listContent,
  mainTypeGroups,
  otherTypes,
} from '@/utils/fileUtils';
import { FileWithPath } from '@mantine/dropzone';

type FileCheckContext = {
  rapidPbrActive: boolean;
  storageLeft: number;
  maxAllowedFileSize: number | null | undefined;
  hasCadSupport: boolean;
  hasAdvancedCadSupport: boolean;
  planImportCost?: number | null;
};

type CheckFilesArgs = {
  files: FileWithPath[];
  context: FileCheckContext;
};

export type GeneralValidationResult = {
  infos: Record<string, string>;
  errors: Record<string, string>;
  warnings: Record<string, string>;
};

const checkInputTypes = (inputTypes: string[]) => {
  const allAllowedTypes = mainTypeGroups.flat().concat(otherTypes);
  const groupsInInput = new Set();

  for (const type of inputTypes) {
    // Check if the type is allowed in any group
    if (!allAllowedTypes.includes(type)) {
      return false;
    }

    // Check if the type belongs to a main group
    for (const group of mainTypeGroups) {
      if (group.includes(type)) {
        // Check if a type from a different main group has already been encountered
        if (groupsInInput.size > 0 && !groupsInInput.has(group)) {
          return false;
        }
        groupsInInput.add(group);
        break;
      }
    }
  }

  // Check if all main groups are represented in the input
  return groupsInInput.size === 1;
};

export const checkRules = (files: FileWithPath[] | string[], context?: CheckFilesArgs['context']) => {
  const result: GeneralValidationResult = {
    infos: {},
    errors: {},
    warnings: {},
  };
  const { fileEndings, mainTypeCount, auxTypeCount, includesCad, jsonCount, includesPBR } = getFilesMeta(files);

  // 1) With multiple main assets info message is shown.
  if (mainTypeCount > 1) {
    result.infos.uploadingMultipleMainTypes =
      'You are uploading multiple 3D models. Each 3D model will appear as a separate Base Asset.';
  } else {
    delete result.infos.uploadingMultipleMainTypes;
  }

  // 2) With multiple main assets mixed with auxiliary assets a blocking error is shown.
  if (mainTypeCount > 1 && auxTypeCount > 0) {
    result.errors.mixedMainAuxType =
      'You are trying to upload multiple 3D models mixed with separate textures or other files. Uploading multiple 3D models together is only possible if you only add 3D models with embedded files. Please remove all individual files that are not a 3D model itself or a .zip file.';
  } else {
    delete result.errors.mixedMainAuxType;
  }

  // 3) With only auxiliary files an error is whown.
  if (auxTypeCount > 0 && mainTypeCount == 0) {
    result.errors.onlyAux =
      'Sorry, uploading individual files without a 3D model is not possible. Please add a 3D model file or upload a combination of a 3D model together with related assets (textures, material files etc.). For more information please refer to the Base Asset Guidelines.';
  } else {
    delete result.errors.onlyAux;
  }

  // 4) With invalid file combinations a warning is shown.
  // This warning is disabled if we already have errors, as it would send mixed signals to the user
  if (auxTypeCount > 0 && Object.keys(result.errors).length === 0) {
    const isAllowed = checkInputTypes(fileEndings);
    if (!isAllowed) {
      result.warnings.notAllowedCombination =
        'Your files will be uploaded, however, there are files in your upload that might be unnecessary. Please refer to base asset guidelines to improve your upload structure.';
    } else {
      delete result.warnings.notAllowedCombination;
    }
  } else {
    delete result.warnings.notAllowedCombination;
  }

  // 5) Show message if CAD uploaded because it costs extra.
  // const withCad = files.find(file => cadTypes.includes(file.extension))
  // if (includesCad && context?.planImportCost === 0) {
  //   result.infos.costCad = 'You are importing a CAD file. This will cost 1 additional RapidPoint.';
  // } else {
  //   delete result.infos.costCad;
  // }

  if (includesPBR) {
    result.infos.versionSupport =
      'DCC Import currently supports 3ds Max versions up to 2025. Please check base asset guidelines for more info.';
  } else {
    delete result.infos.versionSupport;
  }

  // 0) With empty files no infos or errors are shown.
  if (Object.keys(files).length === 0) {
    result.errors = {};
    result.infos = {};
    result.warnings = {};
  }

  return result;
};

export const checkZipRules = (files: string[], context: CheckFilesArgs['context']) => {
  const result = checkRules(files, context);
  const { fileEndings, mainTypeCount, auxTypeCount, includesCad, jsonCount } = getFilesMeta(files);

  if (jsonCount > 1) {
    result.errors.tooMuchMetadata = 'Only one json-metadata-file is allowed per zip file.';
  }

  if (fileEndings.filter((ending) => cadTypes.includes(ending)).length > 0) {
    result.errors.noCadInZip = "CAD files can't be uploaded in zip files. Please upload them directly.";
  }

  // Special handling for asm+prt and iam+ipt combinations
  const hasAsm = fileEndings.includes('asm');
  const hasIam = fileEndings.includes('iam');
  const hasCatproduct = fileEndings.includes('catproduct');
  const prtCount = fileEndings.filter((ending) => ending === 'prt').length;
  const iptCount = fileEndings.filter((ending) => ending === 'ipt').length;
  const catpartCount = fileEndings.filter((ending) => ending === 'catpart').length;

  if (
    mainTypeCount > 1 &&
    !(hasAsm && prtCount > 0) &&
    !(hasIam && iptCount > 0) &&
    !(hasCatproduct && catpartCount > 0)
  ) {
    result.errors.mainTypeLimit =
      'Your archive contains multiple 3D file formats. Please make sure there is one main 3D file format per archived folder.';
  }

  return result;
};

function fileSizeSum(files: FileWithPath[]) {
  const size = Object.values(files).reduce((acc, current) => {
    return acc + current.size;
  }, 0);
  return size;
}

export const checkUploadedFiles = async ({ files, context }: CheckFilesArgs) => {
  // Check rules for all files (without looking in zips)
  const result = { general: checkRules(files, context), files: {} as Record<string, string[]> };

  for (let file of files) {
    const checkResult = await checkSingleFile({
      file,
      context,
    });

    if (checkResult.isSupportedArchive && checkResult.content) {
      const maxFiles = checkResult.content.filter((fileName) => fileName.endsWith('.max'));

      if (maxFiles.length && !context.rapidPbrActive) {
        checkResult.errors.push(errorDCCImportOnPaid);
      }
    }
    if (checkResult.errors.length > 0) result.files[file.name] = checkResult.errors;
  }

  const zipFiles = files.filter((file) => getFileExtension(file) === 'zip');
  for (const file of zipFiles) {
    const zipContent = await listContent(file);
    const zipResult = checkZipRules(zipContent, context);

    if (zipResult.infos.versionSupport) {
      result.general.infos.versionSupport = zipResult.infos.versionSupport;
    }

    if (fileSizeSum(files) >= context.storageLeft) {
      result.general.errors.storageLimit = 'Sorry, your upload exceeds the storage you have left';
    }
  }

  if (Object.keys(result.files).length > 0)
    result.general.errors.invalidFiles =
      'One or more of your files are invalid. Check the error message on the file for more information.';

  return result;
};

export const errorDCCImportOnPaid =
  'DCC Import is only available for paid plans. Please upgrade your account to access this feature.';

const checkSingleFile = async ({
  file,
  context,
}: {
  file: FileWithPath;
  context: FileCheckContext;
}): Promise<{ errors: string[]; isSupportedArchive?: boolean; content?: string[] }> => {
  const errors: string[] = [];

  const meta = getFileMeta(file);
  if (!meta) {
    return { errors: ['This file format is unsupported!'] };
  }

  if (meta.isSupportedArchive) {
    const content = await listContent(file);

    const zipValidationResult = checkZipRules(content, context);
    const zipErrors = Object.values(zipValidationResult.errors);

    if (typeof context.maxAllowedFileSize === 'number' && file.size >= gbToBytes(context.maxAllowedFileSize)) {
      zipErrors.push("Sorry, this archive exceeds your plan's allowed file size.");
    }

    return {
      isSupportedArchive: true,
      content,
      errors: zipErrors,
    };
  }

  if (meta.isArchive && !meta.isSupportedArchive) {
    errors.push('We currently support only .zip uploads for archived formats.');
  }

  if (!context.hasCadSupport && meta.isCad) {
    errors.push('Sorry, but your current plan does not support CAD files. Please upgrade.');
  }

  if (!context.hasAdvancedCadSupport && meta.isAdvancedCad) {
    errors.push('Sorry, but your current plan does not support advanced CAD files. Please upgrade.');
  }

  if (meta.isPBR) {
    if (!context.rapidPbrActive) {
      errors.push(errorDCCImportOnPaid);
    } else {
      errors.push('MAX files and associated files need to be uploaded in a zip archive.');
    }
  }

  if (typeof context.maxAllowedFileSize === 'number' && file.size >= gbToBytes(context.maxAllowedFileSize)) {
    errors.push("Sorry, this file exceeds your plan's allowed file size.");
  }

  return { errors };
};

function gbToBytes(gb: number) {
  return Math.round(gb * 1024 * 1024 * 1024 * 100) / 100;
}
